import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtQty } from "@/lib/pack";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { useAuth } from "@/contexts/AuthContext";
import { X, Save, Search, Filter, Layers, Wand2 } from "lucide-react";

/**
 * Multi-row editor for the `low_stock_threshold` field on inventory
 * items. Manager tool — the WM can walk down a filtered list, punch a
 * number into each row, then hit "Save all". Rows are only PUT'd for
 * items whose value actually changed so the audit log stays quiet.
 *
 * "Suggest from usage" fills in a 4-week supply based on the item's
 * current weekly usage — same maths the Reorder Suggestions page uses,
 * so bulk-editing and single-item reorders stay consistent.
 */
const BulkThresholdsDialog = ({ open, items, onClose, onSaved }) => {
  const { departments } = useDepartments();
  const { user, isManager } = useAuth();
  // v184 — Staff and managers with a primary dept default the filter to
  // that dept so opening the dialog doesn't blast the full catalog at
  // them. Manager can always tap "All depts" to see everything. Staff
  // still get every dept chip (they need the ability to switch between
  // their multiple assigned depts) but not "All".
  const userDepts = Array.isArray(user?.departments) ? user.departments : [];
  const primaryDept = userDepts[0] || null;
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all"); // all | zero | any
  const [deptFilter, setDeptFilter] = useState(primaryDept || "all");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState({}); // stock_id -> weekly

  useEffect(() => {
    if (!open) return;
    setEdits({});
    setSearch("");
    setFilterMode("all");
    // Re-apply the user's default dept whenever the dialog opens so the
    // filter doesn't stick from a previous session.
    setDeptFilter(primaryDept || "all");
    // Pull the reorder suggestions once so we can compute the "Suggest"
    // fills — the endpoint already returns weekly_usage per item.
    api
      .get("/reports/item-consumption?days=60")
      .then((r) => {
        const map = {};
        (r.data?.rows || []).forEach((row) => {
          if (row.id) {
            // qty_pack is total OUT qty in the 60-day window → per-week
            // rate is / 60 * 7. Matches the maths used by the Reorder
            // Suggestions endpoint so single-item and bulk suggestions
            // stay in sync.
            map[row.id] = (Number(row.qty_pack || 0) / 60) * 7;
          }
        });
        setUsage(map);
      })
      .catch(() => setUsage({}));
  }, [open]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items || [])
      .filter((it) => {
        const t = Number(it.low_stock_threshold || 0);
        if (filterMode === "zero" && t !== 0) return false;
        if (filterMode === "any" && t === 0) return false;
        if (deptFilter !== "all") {
          // Items support both a legacy singleton `department` and the
          // canonical `departments[]` — check both so kitchen-consumables
          // tagged to multiple teams appear under each team's filter.
          const list = it.departments?.length
            ? it.departments
            : it.department
              ? [it.department]
              : [];
          if (!list.includes(deptFilter)) return false;
        }
        if (
          q &&
          !`${it.name} ${it.sku || ""} ${it.category || ""} ${it.material || ""}`
            .toLowerCase()
            .includes(q)
        )
          return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, search, filterMode, deptFilter]);

  const setEdit = (id, v) => setEdits((e) => ({ ...e, [id]: v }));

  const suggestOne = (id) => {
    const w = usage[id];
    if (!w || w <= 0) {
      toast.info("No 60-day usage on record for this item");
      return;
    }
    // 4 weeks of usage, rounded to a friendly number.
    const s = Math.max(1, Math.round(w * 4));
    setEdit(id, String(s));
  };

  const suggestAll = () => {
    let filled = 0;
    const next = { ...edits };
    rows.forEach((it) => {
      const w = usage[it.id];
      if (w && w > 0) {
        const s = Math.max(1, Math.round(w * 4));
        // Don't clobber values the user has already typed.
        if (next[it.id] === undefined) {
          next[it.id] = String(s);
          filled += 1;
        }
      }
    });
    setEdits(next);
    toast.success(
      filled
        ? `Suggested thresholds on ${filled} row${filled > 1 ? "s" : ""}`
        : "Nothing to suggest — no usage data on the visible rows",
    );
  };

  const dirtyCount = useMemo(() => {
    return Object.entries(edits).filter(([id, v]) => {
      const it = items.find((x) => x.id === id);
      if (!it) return false;
      const cur = Number(it.low_stock_threshold || 0);
      const nxt = Number(v);
      if (!Number.isFinite(nxt) || v === "") return false;
      return nxt !== cur;
    }).length;
  }, [edits, items]);

  const save = async () => {
    if (!dirtyCount) {
      toast.info("Nothing to save — no thresholds changed");
      return;
    }
    setBusy(true);
    let ok = 0;
    let fail = 0;
    for (const [id, v] of Object.entries(edits)) {
      const it = items.find((x) => x.id === id);
      if (!it) continue;
      const cur = Number(it.low_stock_threshold || 0);
      const nxt = Number(v);
      if (!Number.isFinite(nxt) || v === "" || nxt === cur) continue;
      try {
        await api.put(`/stock/${id}`, { low_stock_threshold: nxt });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setBusy(false);
    if (ok) toast.success(`Updated ${ok} threshold${ok > 1 ? "s" : ""}`);
    if (fail) toast.error(`${fail} row${fail > 1 ? "s" : ""} failed to save`);
    onSaved?.();
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="bulk-thresholds-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(1000px,96vw)] max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b-2 border-black flex items-center justify-between shrink-0">
          <div>
            <div className="label-eyebrow flex items-center gap-1">
              <Layers size={12} strokeWidth={2.75} /> Inventory
            </div>
            <h3 className="font-display text-2xl">Bulk-edit low-stock thresholds</h3>
            <div className="text-[12px] text-[#404040] mt-0.5">
              Walk down the list, punch in the target minimum per item, hit
              Save. Only changed rows get written back.
            </div>
          </div>
          <button
            data-testid="bulk-thresholds-close"
            className="nb-btn bg-white p-2"
            onClick={onClose}
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="px-5 py-3 border-b-2 border-black bg-[#F9FAFB] flex flex-col gap-2 shrink-0">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search
                size={16}
                strokeWidth={2.5}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#607087]"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, SKU, material…"
                data-testid="bulk-thresholds-search"
                className="nb-input pl-9"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { k: "all", label: "All" },
                { k: "zero", label: "No threshold" },
                { k: "any", label: "Has threshold" },
              ].map((f) => (
                <button
                  key={f.k}
                  type="button"
                  onClick={() => setFilterMode(f.k)}
                  data-testid={`bulk-thresholds-filter-${f.k}`}
                  className={`nb-btn px-3 py-2 text-xs flex items-center gap-1 ${
                    filterMode === f.k ? "nb-btn-dark" : "bg-white"
                  }`}
                >
                  <Filter size={12} strokeWidth={2.75} />
                  {f.label}
                </button>
              ))}
              <button
                type="button"
                onClick={suggestAll}
                data-testid="bulk-thresholds-suggest-all"
                className="nb-btn bg-[#FFF7D6] px-3 py-2 text-xs flex items-center gap-1"
                title="Fill blank rows with a 4-week supply based on recent usage"
              >
                <Wand2 size={12} strokeWidth={2.75} /> Suggest all
              </button>
            </div>
          </div>
          <div
            className="flex gap-1.5 flex-wrap items-center"
            data-testid="bulk-thresholds-dept-chips"
          >
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#607087] mr-1">
              Department
            </span>
            <button
              type="button"
              onClick={() => setDeptFilter("all")}
              data-testid="bulk-thresholds-dept-all"
              className={`nb-btn px-3 py-1.5 text-xs ${
                deptFilter === "all" ? "nb-btn-dark" : "bg-white"
              }`}
            >
              All depts
            </button>
            {(departments || [])
              .filter((d) => isManager || userDepts.includes(d.name))
              .map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDeptFilter(d.name)}
                data-testid={`bulk-thresholds-dept-${d.name}`}
                className={`nb-btn px-3 py-1.5 text-xs ${
                  deptFilter === d.name ? "nb-btn-dark" : "bg-white"
                }`}
              >
                {d.name}
              </button>
            ))}
            {primaryDept && deptFilter === primaryDept && (
              <span
                className="text-[10px] font-bold text-[#607087] ml-1"
                data-testid="bulk-thresholds-dept-default-hint"
              >
                · your default
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b-2 border-black text-xs uppercase tracking-wider text-[#607087]">
              <tr>
                <th className="text-left px-4 py-2">Item</th>
                <th className="text-right px-4 py-2 w-24">On hand</th>
                <th className="text-right px-4 py-2 w-32">Weekly use</th>
                <th className="text-right px-4 py-2 w-24">Current</th>
                <th className="text-right px-4 py-2 w-40">New threshold</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => {
                const cur = Number(it.low_stock_threshold || 0);
                const val = edits[it.id];
                const isDirty =
                  val !== undefined &&
                  val !== "" &&
                  Number.isFinite(Number(val)) &&
                  Number(val) !== cur;
                return (
                  <tr
                    key={it.id}
                    data-testid={`bulk-thresholds-row-${it.id}`}
                    className={`border-b border-[#E0E5EA] ${
                      isDirty ? "bg-[#FFF7D6]" : ""
                    }`}
                  >
                    <td className="px-4 py-2">
                      <div className="font-bold">{it.name}</div>
                      <div className="text-[11px] text-[#607087]">
                        {[it.material, it.category, it.type]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono-num">
                      {fmtQty(it.current_qty)} {it.unit}
                    </td>
                    <td className="px-4 py-2 text-right font-mono-num text-[#607087]">
                      {usage[it.id]
                        ? `${fmtQty(usage[it.id])} ${it.unit}/wk`
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-mono-num">
                      {cur === 0 ? (
                        <span className="text-[#8A1F1F]">—</span>
                      ) : (
                        `${fmtQty(cur)} ${it.unit}`
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={val ?? ""}
                          placeholder={cur ? String(cur) : "0"}
                          onChange={(e) => setEdit(it.id, e.target.value)}
                          data-testid={`bulk-thresholds-input-${it.id}`}
                          className="nb-input w-24 text-right font-mono-num"
                        />
                        <button
                          type="button"
                          onClick={() => suggestOne(it.id)}
                          data-testid={`bulk-thresholds-suggest-${it.id}`}
                          className="nb-btn bg-white px-2 py-1.5 text-[10px]"
                          title="Suggest 4-week supply from usage"
                        >
                          <Wand2 size={12} strokeWidth={2.75} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-[#607087]">
                    Nothing matches your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-4 border-t-2 border-black flex items-center gap-3 shrink-0">
          <div
            className="text-xs font-bold text-[#404040]"
            data-testid="bulk-thresholds-dirty-count"
          >
            {dirtyCount} row{dirtyCount === 1 ? "" : "s"} pending
          </div>
          <div className="flex-1" />
          <button onClick={onClose} className="nb-btn bg-white px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !dirtyCount}
            data-testid="bulk-thresholds-save"
            className="nb-btn nb-btn-primary px-5 py-2 text-sm flex items-center gap-1"
          >
            <Save size={14} strokeWidth={2.75} />
            {busy ? "Saving…" : `Save ${dirtyCount || ""}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BulkThresholdsDialog;
